import React, { useCallback, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { selectCurrentUser } from '../../../../../../../store/meetingSummary/user'
import { selectMeetingSoleContact, selectRoomSession } from '../../../../../../../store/meetingSummary/roomSession'
import { selectSelectedMeetingTemplate } from '../../../../../../../store/meetingSummary/meetingTemplates'
import { selectMeetingSummaryAgentAiAgentResultId, setMeetingSummary } from '../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { useLazyGetAiAgentResultQuery } from '../../../../../../../store/ai/aiAgentResult'
import { useGetExternalUserByIdQuery } from '../../../../../../../store/externalUser/externalUser'
import { AIAgentType } from '../../../../../../../models/AIAgent'
import { type User } from '../../../../../../../models/User'
import { aiAgentResultAnchor } from '../../../../../../../store/documentConnectors/documentConnectors'
import {
  DocumentConnectorActionButton,
  type DocumentConnectorActionItem,
} from '../../../../../../../components/DocumentConnector/DocumentConnectorActionButton'
import { getIntegrationDisplayName, type IntegrationServiceId } from '../../../../../../../common/integrations'
import { formatDateYYYYMMDD } from '../../../../../../../common/dateUtils'
import { getConnectedDocumentServiceIds } from '../../../../../../../common/getConnectedDocumentServiceIds'
import { useLastUsedDocumentConnector } from '../../../../../../../common/lastUsedDocumentConnector'
import { SaveToDocumentConnectorModal } from '../../../Header/SaveToDocumentConnectorModal'
import { DocumentExportType } from '../../../../../../../components/DocumentConnector/models'
import { useMeetingLinkedFolder } from '../../../Header/useMeetingLinkedFolder'

interface MeetingSummaryAgentDocumentButtonProps {
  generatePDFBlob: () => Promise<Blob | null>
}

export const MeetingSummaryAgentDocumentButton: React.FC<MeetingSummaryAgentDocumentButtonProps> = ({ generatePDFBlob }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const currentUser = useAppSelector(selectCurrentUser)
  const roomSession = useAppSelector(selectRoomSession)
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const aiAgentResultId = useAppSelector(selectMeetingSummaryAgentAiAgentResultId)
  const [activeServiceId, setActiveServiceId] = useState<IntegrationServiceId | null>(null)
  const [lastUsedServiceId, recordLastUsedServiceId] = useLastUsedDocumentConnector()
  const [triggerGetAiAgentResult] = useLazyGetAiAgentResultQuery()
  const soleContact = useAppSelector(selectMeetingSoleContact)
  const { data: soleContactData } = useGetExternalUserByIdQuery({ accountId, userId: soleContact?.id ?? '' }, { skip: !accountId || !soleContact })
  const [updatedContact, setUpdatedContact] = useState<User | null>(null)
  const contact = updatedContact ?? soleContactData?.data ?? null

  const anchor = aiAgentResultId ? aiAgentResultAnchor(aiAgentResultId) : null

  const connectedDocumentServiceIds = getConnectedDocumentServiceIds(currentUser)
  const { folderIds: initialFolderIds, isLoading: linkedFoldersLoading } = useMeetingLinkedFolder(
    activeServiceId ?? undefined,
    roomSession?.roomId,
    roomSession?.id,
  )

  const dateStr = roomSession?.startedAt ? formatDateYYYYMMDD(roomSession.startedAt) : ''
  const defaultFileName = `Meeting Summary${dateStr ? `_${dateStr}` : ''}.pdf`

  const handleSaved = useCallback(async () => {
    if (!roomSession || !selectedMeetingTemplate?.id) {
      return
    }

    const result = await triggerGetAiAgentResult(
      {
        accountId,
        type: AIAgentType.MEETING_SUMMARY,
        resultKey: `${roomSession.roomId}|${roomSession.id}|${selectedMeetingTemplate?.id}`,
      },
      false,
    ).unwrap()

    if (result) {
      dispatch(setMeetingSummary(result))
    }
  }, [accountId, roomSession, selectedMeetingTemplate, triggerGetAiAgentResult, dispatch])

  if (connectedDocumentServiceIds.length === 0 || !roomSession) {
    return null
  }

  const items: DocumentConnectorActionItem[] = connectedDocumentServiceIds.map((serviceId) => ({
    key: serviceId,
    serviceId,
    label: `Save to ${getIntegrationDisplayName(serviceId)}`,
  }))

  return (
    <>
      {activeServiceId ? (
        <SaveToDocumentConnectorModal
          serviceId={activeServiceId}
          show={true}
          onHide={() => setActiveServiceId(null)}
          generatePDFBlob={generatePDFBlob}
          defaultFileName={defaultFileName}
          initialFolderIds={initialFolderIds}
          initialFoldersReady={!linkedFoldersLoading}
          anchor={anchor}
          saveSyncStatus={anchor != null}
          onSaved={handleSaved}
          contact={contact}
          onContactUpdated={setUpdatedContact}
          exportType={DocumentExportType.MEETING_SUMMARY}
          roomSession={roomSession}
        />
      ) : null}
      <DocumentConnectorActionButton
        items={items}
        primaryKey={lastUsedServiceId ?? undefined}
        onSelect={(key) => {
          const serviceId = key as IntegrationServiceId
          recordLastUsedServiceId(serviceId)
          setActiveServiceId(serviceId)
        }}
        buttonClassName='px-2 py-1 button-medium'
        buttonVariant='header'
      />
    </>
  )
}
